import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { formatCurrency } from '@/utils/currency';
import { descriptorDimensions } from '@/utils/rapidstor';

import store from '@/store';
import {
    ReservationData,
    selectUpsell,
    setReservationData,
    setUnitData,
    setUpsell,
} from '@/store/bookingSlice';

import Button from '@/components/elements/button';

import { ANIMATION_DELAY, ANIMATION_SPEED } from '@/features/rapidstor/animations';
import { descriptorTitle } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { DescriptorOptions } from '@/features/rapidstor/UnitOptions';

import baseClasses from '../styles.module.scss';

interface Props {
    descriptor: Descriptor;
    unitType: UnitType;
    priceDifference: number;
    desktop?: boolean;
    loading: boolean;
    setLoading: (loading: boolean) => void;
}

export default function UpsellCard({
    descriptor,
    unitType,
    priceDifference,
    desktop,
    loading,
}: Props) {
    const location = useLoaderData() as Location;
    const upSell = useSelector(selectUpsell);
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { unitData, reservationData } = useContext(JourneyContext);

    const { t } = useTranslation();

    const priceDifferenceDisplay = useMemo(() => {
        if (!priceDifference || priceDifference === 0) {
            return 'No Price Change';
        }

        if (priceDifference > 0) {
            return `+${formatCurrency(priceDifference ?? 0, location, false, true)}`;
        } else {
            return `-${formatCurrency(priceDifference ?? 0, location, false, true)}`;
        }
    }, [location, priceDifference]);

    const selectUpsellCard = () => {
        if (!unitData) {
            return null;
        }

        const useDescriptorOptions = DescriptorOptions(descriptor, unitData.descriptors, location);
        const upSellDescriptor = useDescriptorOptions.nextSizeUp(
            unitData.descriptors,
            descriptor.area
        );

        
        store.dispatch(
            setUnitData({
                location,
                unitType: unitType,
                descriptor,
                descriptors: unitData.descriptors,
            })
        );
        
        if (upSellDescriptor) {
            store.dispatch(setUpsell(upSellDescriptor));
        }

        /**
         * If the reservationData has a dealObject, try to use that same deal for the upgrade
         * If not, default to the unitType's first deal
         * Else return null
        */
        const dealToUse = reservationData?.dealObject
            ? unitType.deals.find((deal) => deal._id === reservationData.dealObject?._id)
            : unitType.deal || null;
        
        const reservationStoreData: ReservationData = {
            ...reservationData,
            unitType: unitType,
            descriptor,
            deal: dealToUse?._id || '',
            dealObject: dealToUse || undefined,
            coupon: null,
        };

        store.dispatch(setReservationData(reservationStoreData));
        firebaseLogEvent('select_upsell', {});
    };

    const classNames = [
        baseClasses.card,
        desktop ? baseClasses.desktop : baseClasses.mobile,
        baseClasses.upsell,
    ].join(' ');

    return (
        <>
            {upSell && (
                <motion.div
                    layout={true}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                        duration: ANIMATION_SPEED,
                        delay: ANIMATION_DELAY * 2,
                    }}
                    className={`${classNames}`}
                >
                    <div className={`${baseClasses.card_body}`}>
                        <div className={`${baseClasses.upsell_header}`}>
                            <img
                                src={upSell.picture}
                                className={`${baseClasses.mobile}`}
                            />
                            <div className={`${baseClasses.left}`}>
                                <span>{descriptorTitle(upSell, location)[1]}</span>
                                <label>
                                    {descriptorDimensions(upSell.unitTypes[0], location)}
                                </label>
                            </div>
                            <div className={`${baseClasses.right}`}>
                                <span>{priceDifferenceDisplay}</span>

                                {(() => {
                                    if (priceDifference) {
                                        if (priceDifference > 0) {
                                            return (
                                                <label>
                                                    {t('unit_card:extra')}{' '}
                                                    {formatCurrency(
                                                        priceDifference ?? 0,
                                                        location,
                                                        true
                                                    )}
                                                </label>
                                            );
                                        } else {
                                            return (
                                                <label>
                                                    {t('unit_card:save')}{' '}
                                                    {formatCurrency(
                                                        Math.abs(priceDifference ?? 0),
                                                        location,
                                                        true
                                                    )}
                                                </label>
                                            );
                                        }
                                    }
                                })()}
                            </div>
                        </div>
                        <div
                            className={`${baseClasses.upsell_body} ${baseClasses.desktop}`}
                        >
                            <img className={``} src={upSell.picture} />
                        </div>

                        <Button
                            disabled={loading}
                            onclick={() => {
                                selectUpsellCard();
                            }}
                            type={`primary`}
                            size='medium'
                            testId='pay-now'
                        >
                            <label>{t('customise_booking:upgrade')}</label>
                        </Button>
                    </div>
                </motion.div>
            )}
        </>
    );
}
